/**
 * Collection infinite scroll — true AJAX pagination.
 *
 * All products on the current page are rendered server-side and visible
 * immediately (no client-side hiding). When the user scrolls near the
 * bottom, the next Shopify pagination page is fetched via AJAX and its
 * product cards are appended to the grid. This repeats until no more
 * pages exist.
 *
 * The sentinel (#collection-load-more-wrapper) carries data-next-url
 * (Liquid: {{ paginate.next.url }}). A missing / empty attribute means
 * there are no more pages and the sentinel is hidden.
 *
 * Re-initialises when filters replace collection content (facet:content-replaced).
 * Grid column-switcher buttons continue to work throughout.
 */
(function () {
  var STORAGE_KEY        = 'collection_grid_cols';
  var STORAGE_KEY_MOBILE = 'collection_grid_cols_mobile';

  /* ── section helpers ───────────────────────────────────────────────── */

  function getSectionEl(fromSectionId) {
    if (fromSectionId) {
      var el = document.getElementById('shopify-section-' + fromSectionId);
      if (el) return el;
    }
    var grid = document.getElementById('product-grid');
    return grid ? grid.closest('[id^="shopify-section-"]') : null;
  }

  function getSectionId(sectionEl) {
    return sectionEl.id.replace('shopify-section-', '');
  }

  /* ── sentinel (spinner / observer target) ──────────────────────────── */

  function updateSentinel(sectionEl) {
    var sentinel = sectionEl.querySelector('#collection-load-more-wrapper');
    if (!sentinel) return;
    var hasMore = !!(sentinel.getAttribute('data-next-url'));
    sentinel.style.display = hasMore ? 'flex' : 'none';
  }

  /* ── AJAX load next page ────────────────────────────────────────────── */

  function loadNextPage(sectionEl) {
    var sentinel = sectionEl.querySelector('#collection-load-more-wrapper');
    if (!sentinel) return;
    var nextUrl = sentinel.getAttribute('data-next-url');
    if (!nextUrl || sentinel.getAttribute('data-loading')) return;

    sentinel.setAttribute('data-loading', 'true');

    var fetchUrl = new URL(nextUrl, window.location.href);
    fetchUrl.searchParams.set('section_id', getSectionId(sectionEl));

    fetch(fetchUrl.toString())
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc  = new DOMParser().parseFromString(html, 'text/html');
        var grid = sectionEl.querySelector('#product-grid');
        var newGrid = doc.querySelector('#product-grid');

        /* Append new product cards */
        if (grid && newGrid) {
          Array.from(newGrid.querySelectorAll('.product-card')).forEach(function (card) {
            grid.appendChild(document.importNode(card, true));
          });
        }

        /* Advance the sentinel to the next page URL (or remove it) */
        var newSentinel = doc.querySelector('#collection-load-more-wrapper');
        var newNextUrl  = newSentinel ? (newSentinel.getAttribute('data-next-url') || '') : '';
        if (newNextUrl) {
          sentinel.setAttribute('data-next-url', newNextUrl);
        } else {
          sentinel.removeAttribute('data-next-url');
        }
        updateSentinel(sectionEl);
      })
      .catch(function (err) {
        console.error('[collection-load-more] fetch error:', err);
      })
      .finally(function () {
        sentinel.removeAttribute('data-loading');
      });
  }

  /* ── infinite scroll via IntersectionObserver ───────────────────────── */

  function setupInfiniteScroll(sectionEl) {
    var sentinel = sectionEl.querySelector('#collection-load-more-wrapper');
    if (!sentinel) return;

    if (sectionEl._infiniteScrollObserver) {
      sectionEl._infiniteScrollObserver.disconnect();
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          loadNextPage(sectionEl);
        }
      });
    }, { rootMargin: '0px', threshold: 0.1 });

    observer.observe(sentinel);
    sectionEl._infiniteScrollObserver = observer;
  }

  /* ── grid column switcher ───────────────────────────────────────────── */

  function setGridClass(sectionEl, cols) {
    if (!sectionEl) return;
    sectionEl.classList.remove(
      'collection--grid-3', 'collection--grid-4',
      'collection--grid-5', 'collection--grid-6',
      'active_cards'
    );
    sectionEl.classList.add('collection--grid-' + cols);
    if (cols === 6) sectionEl.classList.add('active_cards');
    try {
      localStorage.setItem(STORAGE_KEY, String(cols));
      if (window.matchMedia && window.matchMedia('(max-width: 999px)').matches) {
        localStorage.setItem(STORAGE_KEY_MOBILE, String(cols));
      }
    } catch (e) {}
  }

  function onGridButtonClick(btn) {
    var sectionEl = btn.closest('[id^="shopify-section-"]');
    if (!sectionEl) return;
    var cols = parseInt(btn.getAttribute('data-cols'), 10);
    if (isNaN(cols) || cols < 3 || cols > 6) return;
    setGridClass(sectionEl, cols);
    sectionEl.querySelectorAll('.collection__grid-toolbar').forEach(function (toolbar) {
      toolbar.querySelectorAll('.collection__grid-view-btns button').forEach(function (b) {
        var c = parseInt(b.getAttribute('data-cols'), 10);
        b.classList.toggle('is-active', c === cols);
        b.setAttribute('aria-pressed', c === cols ? 'true' : 'false');
      });
    });
  }

  /* ── init ───────────────────────────────────────────────────────────── */

  function init(sectionId) {
    var sectionEl = getSectionEl(sectionId);
    if (!sectionEl) return;

    /* Restore saved column preference */
    var isMobile  = !!(window.matchMedia && window.matchMedia('(max-width: 999px)').matches);
    var savedCols = 4;
    try {
      savedCols = parseInt(
        isMobile
          ? (localStorage.getItem(STORAGE_KEY_MOBILE) || '4')
          : (localStorage.getItem(STORAGE_KEY) || '4'),
        10
      );
    } catch (e) {}
    if (!isMobile && savedCols === 3) savedCols = 4;
    if (savedCols >= 3 && savedCols <= 6) {
      setGridClass(sectionEl, savedCols);
      sectionEl.querySelectorAll('.collection__grid-view-btns button').forEach(function (btn) {
        var c = parseInt(btn.getAttribute('data-cols'), 10);
        btn.classList.toggle('is-active', c === savedCols);
        btn.setAttribute('aria-pressed', c === savedCols ? 'true' : 'false');
      });
    }

    updateSentinel(sectionEl);
    setupInfiniteScroll(sectionEl);
  }

  /* ── event delegation ───────────────────────────────────────────────── */

  document.addEventListener('click', function (e) {
    var gridBtn = e.target.closest('.collection__grid-view-btns button[data-cols]');
    if (gridBtn) {
      e.preventDefault();
      onGridButtonClick(gridBtn);
    }
  });

  /* ── boot ───────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Re-init after facet filter replaces collection content */
  document.addEventListener('facet:content-replaced', function (e) {
    var sectionId = e.detail && e.detail.sectionId;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        init(sectionId);
      });
    });
  });
})();
